/**
 * เปิด URL ในเบราว์เซอร์ระบบ (Safari/Chrome) จาก LINE LIFF WebView
 * — ไม่ใช่แค่แท็บ/หน้าต่างใน WebView เดียวกัน
 */

import { getCanonicalOrigin } from "@/lib/getCanonicalOrigin";

/** LIFF ID สำหรับเปิด OAuth ภายนอก — แยก Aera กับหน้า /signin (เดิม /login) */
function resolveLiffIdForExternalBrowser(): string {
  if (typeof window === "undefined") return "";
  const path = window.location.pathname || "";
  if (path.startsWith("/signin")) {
    return (process.env.NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN || "").trim();
  }
  const isConnectFlow =
    path.startsWith("/connect") || path.startsWith("/integrations");
  if (isConnectFlow) {
    return (process.env.NEXT_PUBLIC_LINE_LIFF_ID_CONNECT || "").trim();
  }
  const isAeraFlow =
    path.startsWith("/login/aera") ||
    path.startsWith("/auth/google/callback-aera");
  if (isAeraFlow) {
    return (process.env.NEXT_PUBLIC_LINE_LIFF_ID_LOGIN_AERA || "").trim();
  }
  return (process.env.NEXT_PUBLIC_LINE_LIFF_ID_LOGIN || "").trim();
}

export type LineLiffSdk = {
  init: (config: {
    liffId: string;
    withLoginOnExternalBrowser?: boolean;
  }) => Promise<void>;
  ready?: Promise<unknown>;
  isInClient?: () => boolean;
  getContext?: () => { type?: string } | null;
  openWindow?: (
    params: { url: string; external?: boolean } | string,
  ) => void | Promise<unknown>;
  openExternalUrl?: (url: string) => void | Promise<void>;
};

export type LiffSigninEnvProbe = {
  inClient: boolean;
  contextType: string | null;
};

/** UA ของ LIFF browser มีสตริง `LIFF` — ไม่มีใน LINE In-app browser (เอกสาร LINE) */
export function isLineLiffBrowserUserAgent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return /LIFF/i.test(window.navigator.userAgent || "");
  } catch {
    return false;
  }
}

/** หลัง `liff.init` — อ่าน `isInClient` + `getContext().type` (ใน In-app browser จะเป็น `external`) */
export function readLiffSigninEnvProbe(liff: LineLiffSdk): LiffSigninEnvProbe {
  const inClient = Boolean(liff.isInClient?.());
  let contextType: string | null = null;
  try {
    const ctx = liff.getContext?.();
    if (ctx && typeof ctx.type === "string") contextType = ctx.type;
  } catch {
    // ignore
  }
  return { inClient, contextType };
}

/** Shared LIFF script loader (single `data-liff-sdk` tag on the page). */
export function loadLiffSdk(): Promise<LineLiffSdk> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("LIFF ใช้งานได้เฉพาะบน browser"));
  }

  const w = window as unknown as { liff?: LineLiffSdk };
  if (w.liff) return Promise.resolve(w.liff);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-liff-sdk="true"]',
    );
    if (existing) {
      existing.addEventListener("load", () => {
        if (w.liff) resolve(w.liff);
        else reject(new Error("ไม่พบ LIFF SDK"));
      });
      existing.addEventListener(
        "error",
        () => reject(new Error("โหลด LIFF SDK ไม่สำเร็จ")),
        { once: true },
      );
      if (w.liff) resolve(w.liff);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://static.line-scdn.net/liff/edge/2/sdk.js";
    script.async = true;
    script.dataset.liffSdk = "true";
    script.onload = () => {
      if (w.liff) resolve(w.liff);
      else reject(new Error("ไม่พบ LIFF SDK หลังจากโหลดเสร็จ"));
    };
    script.onerror = () => reject(new Error("โหลด LIFF SDK ไม่สำเร็จ"));
    document.head.appendChild(script);
  });
}

export function isInLineLiffClient(): boolean {
  if (typeof window === "undefined") return false;
  const liffObj = (window as { liff?: { isInClient?: () => boolean } }).liff;
  try {
    if (typeof liffObj?.isInClient === "function")
      return Boolean(liffObj.isInClient());
  } catch {
    // ignore
  }
  return /Line\//i.test(window.navigator.userAgent);
}

/**
 * True only when the LIFF JS SDK is present and `liff.isInClient()` is true.
 * Never infers from User-Agent — LINE in-app browser alone is not treated as LIFF.
 * Prefer calling after `liff.init()` (before init, this is usually `false`).
 */
export function isLiffSdkReportedInClient(): boolean {
  if (typeof window === "undefined") return false;
  const liffObj = (window as { liff?: { isInClient?: () => boolean } }).liff;
  try {
    if (typeof liffObj?.isInClient === "function")
      return Boolean(liffObj.isInClient());
  } catch {
    // ignore
  }
  return false;
}

/** อยู่บนโฮสต์เกตเวย์ของ LINE LIFF (ก่อน/ระหว่างเปิด endpoint) — ไม่ใช่แค่ in-app บนโดเมนของแอป */
export function isLiffLineMeGatewayHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "liff.line.me";
}

export type SigninClientEnvKind =
  | "browser"
  | "liff_mini_app"
  | "liff_gateway"
  | "line_in_app_webview";

/**
 * จำแนกทันทีจาก UA / host / SDK (ก่อน `liff.init` ถ้าจำเป็น)
 * — LIFF browser มี `LIFF` ใน UA; In-app browser มี `Line/` แต่ไม่มี `LIFF`
 */
export function probeSigninClientEnvSync(): SigninClientEnvKind | null {
  if (typeof window === "undefined") return null;
  if (isLiffSdkReportedInClient()) return "liff_mini_app";
  if (isLiffLineMeGatewayHost()) return "liff_gateway";
  if (isLineLiffBrowserUserAgent()) return "liff_mini_app";
  if (!looksLikeLineEmbeddedBrowser()) return "browser";
  const ua = window.navigator.userAgent || "";
  if (/Line\//i.test(ua)) return "line_in_app_webview";
  return null;
}

/** จำแนก LIFF mini app vs LINE In-app WebView (หลัง init SDK ถ้าจำเป็น) */
export async function probeSigninClientEnvAsync(): Promise<SigninClientEnvKind> {
  const syncKind = probeSigninClientEnvSync();
  if (syncKind !== null) return syncKind;

  const liffId = (process.env.NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN || "").trim();
  if (!liffId || !liffId.includes("-")) {
    return resolveSigninClientEnvKindAfterLiffProbe(false);
  }

  try {
    const liff = await loadLiffSdk();
    await liff.init({ liffId, withLoginOnExternalBrowser: false });
    if (liff.ready) await liff.ready;
    const probe = readLiffSigninEnvProbe(liff);
    return resolveSigninClientEnvKindAfterLiffProbe(
      probe.inClient,
      probe.contextType,
    );
  } catch {
    return resolveSigninClientEnvKindAfterLiffProbe(false, null);
  }
}

export function isSigninLiffClientEnv(kind: SigninClientEnvKind): boolean {
  return kind === "liff_mini_app" || kind === "liff_gateway";
}

/**
 * หลัง `liff.init` — `isInClient()` + `getContext().type`
 * (ใน LINE In-app browser `type` เป็น `external` แม้ `isInClient()` เป็น false)
 */
export function resolveSigninClientEnvKindAfterLiffProbe(
  inLiffClient: boolean,
  contextType?: string | null,
): SigninClientEnvKind {
  if (inLiffClient) return "liff_mini_app";
  if (contextType && contextType !== "external") return "liff_mini_app";
  if (typeof window !== "undefined" && isLiffLineMeGatewayHost()) {
    return "liff_gateway";
  }
  if (isLineLiffBrowserUserAgent()) return "liff_mini_app";
  if (looksLikeLineEmbeddedBrowser()) return "line_in_app_webview";
  return "browser";
}

export function isSigninLineInAppWebView(kind: SigninClientEnvKind): boolean {
  return kind === "line_in_app_webview";
}

/**
 * Modal แจ้งเปิดเบราว์เซอร์ภายนอก — เฉพาะ LINE In-app WebView บนโดเมนแอป
 * ไม่แสดงบนเบราว์เซอร์ทั่วไป, LIFF mini app (`isInClient`), หรือ `liff.line.me`
 */
export function shouldShowSigninLineWebViewExternalModal(
  kind: SigninClientEnvKind,
): boolean {
  if (kind !== "line_in_app_webview") return false;
  if (isLiffSdkReportedInClient()) return false;
  if (typeof window !== "undefined" && isLiffLineMeGatewayHost()) {
    return false;
  }
  return true;
}

/** ลิงก์ LIFF / gateway — อยู่ใน LINE ไม่เปิด external อัตโนมัติ */
export function shouldStayInsideLineForSignin(kind: SigninClientEnvKind): boolean {
  return kind === "liff_mini_app" || kind === "liff_gateway";
}

function signinUrlWithOpenExternalBrowserParam(pageUrl: string): string {
  try {
    const u = new URL(pageUrl, window.location.origin);
    u.searchParams.set("openExternalBrowser", "1");
    return u.toString();
  } catch {
    return pageUrl;
  }
}

/** ลบ openExternalBrowser ออกจากแถบ URL หลังเปิดใน Safari/Chrome แล้ว */
export function stripSigninOpenExternalBrowserFromUrlBar(): void {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("openExternalBrowser")) return;
    u.searchParams.delete("openExternalBrowser");
    const qs = u.searchParams.toString();
    const next = `${u.pathname}${qs ? `?${qs}` : ""}${u.hash}`;
    window.history.replaceState(null, "", next);
  } catch {
    // ignore
  }
}

/**
 * เปิด `/signin` ใน Safari/Chrome จาก LINE In-app WebView
 * — ต้องเรียกแบบ sync ใน onClick (user gesture) มิฉะนั้น iOS/LINE มักบล็อก
 */
export function openSigninInSystemBrowserFromLineWebView(
  pageUrl: string,
): boolean {
  if (typeof window === "undefined") return false;

  const target = signinUrlWithOpenExternalBrowserParam(pageUrl);

  if (tryAndroidChromeExternalView(target)) return true;
  if (tryAndroidBrowserIntent(target)) return true;
  if (tryAnchorBlankOpen(target)) return true;

  try {
    const opened = window.open(target, "_blank", "noopener,noreferrer");
    if (opened) return true;
  } catch {
    // ignore
  }

  return tryLiffUrlOpenExternalBrowser(pageUrl);
}

export function signinClientEnvLabelForKind(kind: SigninClientEnvKind): string {
  switch (kind) {
    case "liff_mini_app":
      return "LINE LIFF browser (mini app)";
    case "liff_gateway":
      return "liff.line.me (เกตเวย์ LIFF)";
    case "line_in_app_webview": {
      try {
        const host = window.location.hostname || "";
        return host
          ? `LINE In-app browser (WebView) · ${host}`
          : "LINE In-app browser (WebView)";
      } catch {
        return "LINE In-app browser (WebView)";
      }
    }
    default:
      return "เบราว์เซอร์ทั่วไป";
  }
}

/**
 * referrer หลัง OAuth ใน Safari/Chrome มักเป็น access.line.me — ห้ามถือว่าเป็น LINE in-app
 * (แยกจาก liff.line.me / ลิงก์แชท line.me)
 */
export function isLineInAppDocumentReferrer(ref: string): boolean {
  const raw = ref?.trim();
  if (!raw) return false;
  try {
    const host = new URL(raw).hostname.toLowerCase();
    if (host === "access.line.me") return false;
    if (host === "liff.line.me" || host.endsWith(".liff.line.me")) return true;
    if (host === "line.me" || host.endsWith(".line.me")) return true;
    if (host === "line.app" || host.endsWith(".line.app")) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * ลิงก์จากแชท LINE เปิดใน in-app browser บางรุ่นใช้ UA ใกล้ Safari/Chrome ไม่มี "Line/"
 * — ใช้หลายสัญญาณร่วมกัน
 */
export function looksLikeLineEmbeddedBrowser(): boolean {
  if (typeof window === "undefined") return false;
  // In LIFF, the visible host is always liff.line.me.
  if (window.location.hostname === "liff.line.me") return true;
  if (isInLineLiffClient()) return true;

  try {
    const ua = window.navigator.userAgent || "";
    if (/Line\//i.test(ua)) return true;
    const lower = ua.toLowerCase();
    if (lower.includes("liff")) return true;
  } catch {
    // ignore
  }

  try {
    const q = new URLSearchParams(window.location.search);
    // อย่าใช้ openExternalBrowser=1 — มักติด URL หลังเปิด Safari แล้ว ทำให้จำแนกผิดเป็น WebView
    if (q.get("fromLine") === "1") return true;
  } catch {
    // ignore
  }

  try {
    if (isLineInAppDocumentReferrer(document.referrer || "")) return true;
  } catch {
    // ignore
  }

  try {
    const nav = navigator as Navigator & {
      userAgentData?: { brands?: { brand: string }[] };
    };
    const brands = nav.userAgentData?.brands;
    if (Array.isArray(brands)) {
      if (brands.some((b) => /line/i.test(b.brand))) return true;
    }
  } catch {
    // ignore
  }

  if (looksLikeAndroidEmbeddedWebView()) return true;

  return false;
}

/**
 * สัญญาณว่าอยู่ใน LINE/LIFF จริง (ไม่รวม referrer จากลิงก์ที่เปิดใน Safari/Chrome)
 * — ใช้ตัดสิน auto-route ไป Billin หลัง OAuth
 */
export function hasStrongLineInAppSignal(): boolean {
  if (typeof window === "undefined") return false;
  if (window.location.hostname === "liff.line.me") return true;
  if (isLiffSdkReportedInClient()) return true;
  try {
    const ua = window.navigator.userAgent || "";
    if (/Line\//i.test(ua)) return true;
    if (ua.toLowerCase().includes("liff")) return true;
    if (new URLSearchParams(window.location.search).get("fromLine") === "1") {
      return true;
    }
  } catch {
    // ignore
  }
  if (looksLikeAndroidEmbeddedWebView()) return true;
  return false;
}

function normalizeAppRelativePath(appPath: string): string {
  const raw = (appPath || "/").trim() || "/";
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const u = new URL(raw);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return "/";
    }
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
}

/**
 * ใน LINE/LIFF ใช้ `liff.line.me/{id}?path=` (middleware + LiffPathRedirect รองรับ)
 * — `replace` ตรงไป canonical origin จาก LIFF mini app มักไม่เปลี่ยนหน้า (ค้าง overlay)
 */
export function assignLiffDeepLinkIfLineWebView(appPath: string): boolean {
  if (typeof window === "undefined") return false;
  if (!looksLikeLineEmbeddedBrowser()) return false;
  const rel = normalizeAppRelativePath(appPath);
  const liffId = (process.env.NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN || "").trim();
  if (!liffId || !liffId.includes("-")) {
    const baseOrigin = getCanonicalOrigin() || window.location.origin;
    window.location.replace(`${baseOrigin}${rel}`);
    return true;
  }
  const encoded = encodeURIComponent(rel);
  window.location.replace(`https://liff.line.me/${liffId}?path=${encoded}`);
  return true;
}

/**
 * In LINE/LIFF (and some in-app browsers), `router.push` / client transitions are unreliable.
 * Use a full document navigation instead. Returns `true` if navigation was triggered.
 */
export function assignLocationIfLineWebView(path: string): boolean {
  return assignLiffDeepLinkIfLineWebView(path);
}

/**
 * Full navigation ไป path ในแอปโดยตรง (canonical origin) — ไม่ผ่าน `liff.line.me?path=`
 * ใช้หลัง logout / session หมด เมื่ออยู่ในแอปแล้ว — ลดโอกาส LINE WebView เปิดหน้าต่างใหม่
 */
export function assignInPlaceAppLocationIfLineWebView(appPath: string): boolean {
  if (typeof window === "undefined") return false;
  if (!looksLikeLineEmbeddedBrowser()) return false;
  const rel = normalizeAppRelativePath(appPath);
  const baseOrigin = getCanonicalOrigin() || window.location.origin;
  window.location.replace(`${baseOrigin}${rel}`);
  return true;
}

/** ปลายทางภายในแอปที่เป็นหน้า `/signin` (รวม query) — ใช้ก่อน `navigateToSignin` เมื่อ path มาจากข้อมูลภายนอก */
export function isAppSigninPath(path: string): boolean {
  if (!path || typeof path !== "string") return false;
  const p = path.trim();
  if (
    p === "/signin" ||
    p.startsWith("/signin?") ||
    p.startsWith("/signin#") ||
    p.startsWith("/signin/")
  ) {
    return true;
  }
  if (
    typeof window !== "undefined" &&
    (p.startsWith("http://") || p.startsWith("https://"))
  ) {
    try {
      const u = new URL(p);
      if (u.origin !== window.location.origin) return false;
      return u.pathname === "/signin" || u.pathname.startsWith("/signin/");
    } catch {
      return false;
    }
  }
  return false;
}

function normalizeSigninRelativePath(signinPath: string): string {
  const raw = (signinPath || "/signin").trim() || "/signin";
  if (
    typeof window !== "undefined" &&
    (raw.startsWith("http://") || raw.startsWith("https://"))
  ) {
    if (isAppSigninPath(raw)) {
      try {
        const u = new URL(raw);
        return `${u.pathname}${u.search}${u.hash}`;
      } catch {
        return "/signin";
      }
    }
    return "/signin";
  }
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return isAppSigninPath(withSlash) ? withSlash : "/signin";
}

/**
 * ใน LINE/LIFF: นำทางไป `https://liff.line.me/{NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN}?path=...` แล้วคืน `true`
 * เบราว์เซอร์ทั่วไป: คืน `false` (ให้ใช้ `navigateToSignin` หรือ `router.push`)
 *
 * @param signinPath เช่น `/signin` หรือ `/signin?callbackUrl=...`
 */
export function assignSigninLiffIfLineWebView(signinPath = "/signin"): boolean {
  if (typeof window === "undefined") return false;
  if (!looksLikeLineEmbeddedBrowser()) return false;
  return assignLiffDeepLinkIfLineWebView(normalizeSigninRelativePath(signinPath));
}

export type SigninNavigationRouter = {
  push: (href: string) => void;
  replace?: (href: string) => void;
};

/** LINE → LIFF signin; เบราว์เซอร์ → `router.push` หรือ `router.replace` */
export function navigateToSignin(
  router: SigninNavigationRouter,
  signinPath = "/signin",
  options?: { replace?: boolean },
): void {
  const path = normalizeSigninRelativePath(signinPath);
  if (typeof window !== "undefined") {
    // LIFF mini app อยู่บนโดเมนแอปแล้ว — ไม่วน liff.line.me (เดียวกับ logout / in-app redirect)
    if (isLiffSdkReportedInClient() && assignInPlaceAppLocationIfLineWebView(path)) {
      return;
    }
    if (assignSigninLiffIfLineWebView(path)) {
      return;
    }
  }
  if (options?.replace && router.replace) {
    router.replace(path);
  } else {
    router.push(path);
  }
}

/** เดสก์ท็อปจริง ๆ — ไม่ใช่มือถือ/แท็บเล็ตที่แกล้งเป็น Mac */
function isClearlyDesktopBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod|Android|Mobile|Tablet|IEMobile|webOS/i.test(ua)) {
    return false;
  }
  return /Windows NT|Macintosh|X11|Linux x86_64|CrOS|Win64/i.test(ua);
}

/** WebView บน Android (รวม LINE in-app ที่ UA ไม่มีคำว่า Line) */
function looksLikeAndroidEmbeddedWebView(): boolean {
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua) && /\bwv\b|; wv\)/i.test(ua);
}

function tryAndroidBrowserIntent(httpsUrl: string, chromePackage?: string): boolean {
  if (typeof window === "undefined") return false;
  if (!/Android/i.test(navigator.userAgent)) return false;
  try {
    const u = new URL(httpsUrl);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const scheme = u.protocol.replace(":", "");
    const hostPath = `${u.host}${u.pathname}${u.search}${u.hash}`;
    const fallback = encodeURIComponent(httpsUrl);
    const pkg = chromePackage
      ? `;package=${chromePackage}`
      : "";
    const intent = `intent://${hostPath}#Intent;scheme=${scheme};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE${pkg};S.browser_fallback_url=${fallback};end`;
    window.location.href = intent;
    return true;
  } catch {
    return false;
  }
}

/** เปิดลิงก์ HTTPS ใน Chrome ระบบ (หนี LINE in-app WebView บน Android) */
function tryAndroidChromeExternalView(httpsUrl: string): boolean {
  return tryAndroidBrowserIntent(httpsUrl, "com.android.chrome");
}

/** Anchor click ใน user gesture — บาง WebView (รวม iOS) เปิดแท็บ/browser ภายนอกได้ */
function tryAnchorBlankOpen(httpsUrl: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const a = document.createElement("a");
    a.href = httpsUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch {
    return false;
  }
}

/**
 * กดปุ่มจาก LINE in-app WebView → ใช้ LIFF URL + openExternalBrowser (ครั้งเดียว ไม่ auto)
 * ตามเอกสาร LINE ช่วยเปิด endpoint ในเบราว์เซอร์ภายนอก
 */
function tryLiffUrlOpenExternalBrowser(pageUrl: string): boolean {
  if (typeof window === "undefined") return false;
  const liffId = resolveLiffIdForExternalBrowser();
  if (!liffId || !liffId.includes("-")) return false;
  try {
    const u = new URL(pageUrl, window.location.origin);
    u.searchParams.set("openExternalBrowser", "1");
    const path = encodeURIComponent(
      `${u.pathname}${u.search}${u.hash}`,
    );
    window.location.replace(
      `https://liff.line.me/${liffId}?path=${path}&openExternalBrowser=1`,
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * ลิงก์ https จากแชท (ไม่ใช่ liff.line.me) บางเคส isInClient ยัง false — redirect ผ่าน LIFF URL ครั้งเดียวให้ LINE สร้าง context ที่ถูก
 */
function tryOneShotLiffLineMeBootstrap(overridePath?: string): boolean {
  if (typeof window === "undefined") return false;
  const liffId = resolveLiffIdForExternalBrowser();
  if (!liffId) return false;
  if (isClearlyDesktopBrowser()) return false;

  const ua = navigator.userAgent || "";
  const strongLine = /Line\//i.test(ua) || looksLikeLineEmbeddedBrowser();
  if (!strongLine) return false;

  try {
    const w = window as unknown as { liff?: { isInClient?: () => boolean } };
    if (w.liff?.isInClient?.() === true) return false;
  } catch {
    // continue
  }

  try {
    const key = "liff_line_me_bootstrap_v1";
    if (sessionStorage.getItem(key) === "1") return false;
    sessionStorage.setItem(key, "1");
  } catch {
    return false;
  }

  const nextPath =
    typeof overridePath === "string" && overridePath
      ? overridePath
      : `${window.location.pathname}${window.location.search}`;
  const path = encodeURIComponent(nextPath);
  window.location.replace(`https://liff.line.me/${liffId}?path=${path}`);
  return true;
}

/** init ล้มเหลวจนไม่มี liff แต่ยังอยู่ LINE in-app — เข้า liff.line.me ให้ได้ context */
function tryLiffEntryWhenLiffFailedToLoad(): boolean {
  if (typeof window === "undefined") return false;
  const liffId = resolveLiffIdForExternalBrowser();
  if (!liffId || isClearlyDesktopBrowser()) return false;
  const ua = navigator.userAgent || "";
  const strongLine = /Line\//i.test(ua) || looksLikeLineEmbeddedBrowser();
  if (!strongLine) return false;
  try {
    const key = "liff_line_me_bootstrap_v1";
    if (sessionStorage.getItem(key) === "1") return false;
    sessionStorage.setItem(key, "1");
  } catch {
    return false;
  }
  const path = encodeURIComponent(
    `${window.location.pathname}${window.location.search}`,
  );
  window.location.replace(`https://liff.line.me/${liffId}?path=${path}`);
  return true;
}

/** โหลด LIFF และลอง openExternalUrl เฉพาะเมื่อน่าจะอยู่ใน LINE / มี LIFF จริง — ไม่โหลด SDK บน Chrome ทั่วไป */
export function shouldTryLiffExternal(): boolean {
  if (typeof window === "undefined") return false;
  if (window.location.hostname === "liff.line.me") return true;
  if ((window as unknown as { liff?: unknown }).liff) return true;
  if (looksLikeLineEmbeddedBrowser()) return true;

  const liffId = resolveLiffIdForExternalBrowser();
  if (!liffId) return false;

  const ua = window.navigator.userAgent || "";
  // ลิงก์จากแชท LINE บางเคส UA เป็น WebView ธรรมดา — ยังลอง init LIFF ได้ (ถ้าไม่ใช่ LINE จะออกที่ gate isInClient ด้านล่าง)
  return /iPhone|iPad|iPod|Android/i.test(ua);
}

export type OpenLineExternalUrlOptions = {
  /**
   * ข้าม `liff.line.me?path=` bootstrap สำหรับลิงก์ first-party —
   * ถ้าไม่ข้าม ฟังก์ชันอาจ `replace` ใน WebView ไป endpoint ของ LIFF (มัก `/signin`) ก่อน
   * จึงเห็นแว็บหน้า signin แม้ต้องการแค่ `openExternalUrl` ไปหน้าปลายทาง
   */
  skipLiffBootstrap?: boolean;
  /** ส่งเมื่อเปิดจาก LINE In-app WebView (ปุ่ม manual) — ห้าม bootstrap ไป liff.line.me */
  signinClientEnvKind?: SigninClientEnvKind;
};

/**
 * @returns true ถ้าเรียก LIFF API เปิดเบราว์เซอร์ภายนอกแล้ว (หรือ fallback เปิดแท็บ)
 *
 * หมายเหตุ: หน้าเดียวกันมัก init LIFF ไปแล้ว (withLoginOnExternalBrowser: false) —
 * การ init ซ้ำมัก throw; เราจึงไม่ return false ทันที แต่ลอง openExternalUrl / openWindow ต่อ
 */
export async function openLineExternalUrl(
  url: string,
  options?: OpenLineExternalUrlOptions,
): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const liffId = resolveLiffIdForExternalBrowser();
  if (!liffId) return false;
  if (!shouldTryLiffExternal()) return false;

  try {
    const liff = await loadLiffSdk();
    // true = ให้ flow ที่ต้องเปิดนอก WebView (OAuth / consent) ทำงานสอดคล้องเอกสาร LINE
    await liff.init({ liffId, withLoginOnExternalBrowser: true });
    if (liff.ready) await liff.ready;
  } catch {
    // มักเกิดเมื่อ init ถูกเรียกไปแล้วในหน้านี้ — ยังใช้ window.liff ต่อได้
  }

  const liffObj = (window as unknown as { liff?: LineLiffSdk }).liff;
  if (!liffObj) {
    if (
      options?.signinClientEnvKind !== "line_in_app_webview" &&
      tryLiffEntryWhenLiffFailedToLoad()
    ) {
      return true;
    }
    return false;
  }

  let inClient = false;
  try {
    inClient =
      typeof liffObj.isInClient === "function"
        ? Boolean(liffObj.isInClient())
        : false;
  } catch {
    inClient = false;
  }

  // `tryOneShotLiffLineMeBootstrap` is for first-party deep-links into our app
  // (e.g. opening a shared URL from LINE chat) so the LIFF context is established.
  // Explicit `openExternalUrl` opens must skip this — bootstrap stays in-WebView via
  // `liff.line.me` → LIFF Endpoint (often `/signin`) before navigating, causing a flash.
  if (!options?.skipLiffBootstrap) {
    try {
      const target = new URL(url, window.location.origin);
      const isFirstPartyTarget = target.origin === window.location.origin;
      const targetPath = `${target.pathname}${target.search}${target.hash}`;
      if (isFirstPartyTarget && tryOneShotLiffLineMeBootstrap(targetPath)) {
        return true;
      }
    } catch {
      // If URL parsing fails, fall back to existing behavior (skip bootstrap).
    }
  }

  if (
    options?.signinClientEnvKind !== undefined &&
    shouldStayInsideLineForSignin(options.signinClientEnvKind)
  ) {
    return false;
  }

  const fromLineWebView =
    options?.signinClientEnvKind === "line_in_app_webview";

  // เดสก์ท็อปที่ไม่ใช่ LINE: ไม่เรียก LIFF เปิดภายนอก (ให้ openOAuthBridgeUrl ใช้ popup / location แทน)
  if (isClearlyDesktopBrowser() && !inClient && !fromLineWebView) {
    return false;
  }

  if (fromLineWebView && tryAndroidChromeExternalView(url)) {
    return true;
  }

  const openWindow = liffObj.openWindow;
  if (typeof openWindow === "function") {
    try {
      await Promise.resolve(
        openWindow.call(liffObj, { url, external: true }),
      );
      return true;
    } catch {
      /* continue */
    }
    try {
      await Promise.resolve(openWindow.call(liffObj, url));
      return true;
    } catch {
      /* continue */
    }
    try {
      await Promise.resolve(openWindow.call(liffObj, { url }));
      return true;
    } catch {
      /* continue */
    }
  }

  try {
    const openExternalUrl = liffObj.openExternalUrl;
    if (typeof openExternalUrl === "function") {
      await Promise.resolve(openExternalUrl.call(liffObj, url));
      return true;
    }
  } catch {
    // fall through
  }

  // Android WebView / LINE: หนี in-app ไป Chrome ระบบ (ไม่ใช่ Chrome มือถือปกติ)
  const ua = window.navigator.userAgent || "";
  const mayNeedIntentEscape =
    inClient ||
    looksLikeLineEmbeddedBrowser() ||
    /Line\//i.test(ua) ||
    looksLikeAndroidEmbeddedWebView();
  if (mayNeedIntentEscape && tryAndroidChromeExternalView(url)) {
    return true;
  }

  // ไม่ใส่ noopener — บาง WebView ใช้ opener กับ OAuth / ลิงก์ภายนอก
  try {
    const w = window.open(url, "_blank");
    if (w) return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * เปิด URL สาย OAuth / หน้า bridge (`/login/aera/external?auto=1`):
 * - ใน LINE in-app: เปิดเบราว์เซอร์ระบบ (Safari/Chrome) ผ่าน LIFF
 * - ในเบราว์เซอร์ทั่วไป: เปิดหน้าต่าง popup ที่มี `window.opener` (ห้ามใช้ noopener) เพื่อให้ callback ปิด popup แล้วส่ง postMessage กลับหน้าเดิม
 * - ถ้าเปิด popup ไม่ได้: ให้ caller ใช้ `window.location.href` (โฟลว์เดียวกับ redirect เต็มหน้า)
 */
export async function openOAuthBridgeUrl(url: string): Promise<boolean> {
  if (await openLineExternalUrl(url)) {
    return true;
  }
  if (typeof window === "undefined") return false;
  try {
    const opened = window.open(
      url,
      "ailo_google_oauth",
      "width=520,height=700,left=80,top=48,scrollbars=yes,resizable=yes",
    );
    if (opened) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}
